import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  Output,
  QueryList,
  Signal,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
  computed
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subject, Subscription, from, merge, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  pluck,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { DB_PRODUCT_CAPTURE_LABELS } from 'src/app/form-config/mockups/db/db-product-capture-labels.const';
import { DcAlertInnerService } from 'src/app/ui-kit/dc-alert-inner/dc-alert-inner.service';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { FrequencyKeys } from '../../model/consts/frequency.const';
import {
  CallabilityEnum,
  CouponEnum,
  ParticipationBaseEnum,
  ParticipationDownsideEnum,
  ParticipationEnum,
  ParticipationUpsideEnum,
  PayoffEnum,
  ProductCaptureGroupsEnum,
  ScheduleEnum,
  ScheduleType,
  SummaryEnum
} from '../../model/enums/product-capture.enums';
import { SCHEDULE_TYPE_ENUM } from '../../model/enums/schedule-type.enum';
import { ProductCaptureData } from '../../model/interfaces/product-capture.interface';
import { UnderlyingAsset } from '../../model/interfaces/underlying-asset.interface';
import { UnderlyingsScheduler } from '../../model/interfaces/underlyings-scheduler.interface';
import { ProductCaptureService } from '../../services/product-capture.service';
import { GetErrorMessageFromResponse } from '../../utils/get-error-message-from-response';
import { ScheduleFormUtils } from '../../utils/schedule.utils';
import { ScheduleComponent } from '../payoff/schedule/schedule.component';
import { KeyDatesModalService } from '../product-summary/key-dates-modal/key-dates-modal.service';
import { KeyDate } from '../product-summary/key-dates-modal/key-dates-type.const';
import {
  SCHEDULE_STRATEGY_TYPES,
  ScheduleStrategyKeys,
  SchedulesVisibility
} from './event-schedules.const';

const deepEqual = require('deep-equal');

@Component({
  selector: 'app-event-schedules',
  templateUrl: './event-schedules.component.html',
  styleUrls: ['./event-schedules.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DcAlertInnerService],
})
export class EventSchedulesComponent implements AfterViewInit {

  @Input()
  productForm: UntypedFormGroup;

  @Input()
  isEditing: boolean;

  @Input()
  canUserEdit: boolean = true;

  @Input()
  product: ProductCaptureData;

  @HostBinding('class.view-mode')
  viewMode: boolean;

  @ViewChildren(ScheduleComponent)
  scheduleComponents: QueryList<ScheduleComponent>;

  scheduleStrategyItems = SCHEDULE_STRATEGY_TYPES;

  accrualScheduleLagBaseTypes: KeyDate[];

  destroy$ = new Subject();

  form: UntypedFormGroup;

  isRefreshing: boolean;

  labels = DB_PRODUCT_CAPTURE_LABELS;

  schedulesVisibility: SchedulesVisibility;

  // Will be used to unsubscribe in case the scheduleComponents changes
  componentsChanged$: Subscription;

  @Output()
  saveProduct = new EventEmitter();

  constructor(
    private cdr: ChangeDetectorRef,
    private productCaptureService: ProductCaptureService,
    private keyDatesModalService: KeyDatesModalService,
    private dcAlertInnerService: DcAlertInnerService,
    private modalService: ModalService,
    private injector: Injector
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('canUserEdit' in changes) {
      this.viewMode = !changes.canUserEdit.currentValue;
    }

    if ('productForm' in changes) {
      this.form = this.summaryForm
        .get(SummaryEnum.schedule) as UntypedFormGroup;

      this.setSchedulesVisibility();
    }
  }

  setSchedulesVisibility(): void {
    if (this.schedulesVisibility) {
      return;
    }

    const upside = this.featureTypeChanged(this.participation.get(ParticipationEnum.upside).get(ParticipationUpsideEnum.type));
    const downside = this.featureTypeChanged(this.participation.get(ParticipationEnum.downside).get(ParticipationDownsideEnum.downsideType));
    const base = this.featureTypeChanged(this.participation.get(ParticipationEnum.base).get(ParticipationBaseEnum.type));

    this.schedulesVisibility = {
      [ScheduleType.COUPON]: this.validFeatureStrategy(ScheduleType.COUPON),
      [ScheduleType.CALLABILITY]: this.validFeatureStrategy(ScheduleType.CALLABILITY),
      [ScheduleType.PARTICIPATION]: computed(() => upside() || downside() || base())
    }
  }

  removeInvalidSchedules(): void {
    Object.keys(this.schedulesVisibility).forEach((key) => {
      if (!this.schedulesVisibility[key]()) {
        this.form.get(key).reset();
      }
    });
  }

  validFeatureStrategy(key: ScheduleType): Signal<boolean> {
    const scheduleStrategy = toSignal(
      this.scheduleStrategy.valueChanges.pipe(takeUntil(this.destroy$))
    , { initialValue: this.scheduleStrategy.value, injector: this.injector });

    const control = key === ScheduleType.COUPON 
      ? this.coupon.get(CouponEnum.couponType) 
      : this.callability.get(CallabilityEnum.callEventType);

    const exclusiveStrategy = key === ScheduleType.COUPON 
      ? ScheduleStrategyKeys.CALL_ONLY 
      : ScheduleStrategyKeys.COUPON_ONLY;

    const featureChanged = this.featureTypeChanged(control);

    return computed(() => featureChanged() && scheduleStrategy() && scheduleStrategy() !== exclusiveStrategy);
  }

  featureTypeChanged(control: AbstractControl): Signal<boolean> {
    return toSignal(control.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          startWith(control.value),
          map((value) => value && value !== 'NONE'),
      ), { injector: this.injector }
    );
  }

  setupCommonFieldsOnFormChanges(): void {
    this.componentsChanged$?.unsubscribe();

    this.buildObservableForFeatureTypeChange(
        this.coupon.get(CouponEnum.couponType),
        this.coupon.get(CouponEnum.frequency),
        this.callability.get(CallabilityEnum.callEventType),
        this.callability.get(CallabilityEnum.frequency),
    ).pipe(
      takeUntil(this.destroy$),
      debounceTime(250),
      
    )
    .subscribe(() => { 
      this.filterScheduleStrategies();
      this.applyScheduleStrategyDefaultValue();
    })
    .add(this.componentsChanged$);

  }

  buildObservableForFeatureTypeChange(...controls: AbstractControl[]): Observable<any> {
    return merge(
        ...controls.reduce((acc: Observable<any>[], control: AbstractControl) => {
            acc.push(
              control.valueChanges.pipe(
                startWith(control.value),
                takeUntil(this.destroy$),
                distinctUntilChanged(),
              )
            );

            return acc;
          }, [])
        );
  }

  filterScheduleStrategies(): void {
    const couponType = this.couponType?.toUpperCase() ?? 'NONE';
    const callEventType = this.callEventType?.toUpperCase() ?? 'NONE';

    let allowedStrategies = SCHEDULE_STRATEGY_TYPES;

    if (couponType === 'NONE') {
      allowedStrategies = allowedStrategies.filter((strategy) => strategy.id === ScheduleStrategyKeys.CALL_ONLY);
    }

    if (callEventType === 'NONE') {
      allowedStrategies = allowedStrategies.filter((strategy) => strategy.id === ScheduleStrategyKeys.COUPON_ONLY);
    }

    this.scheduleStrategyItems = allowedStrategies;
  }

  applyScheduleStrategyDefaultValue(): void {
    if (this.isEditing) {
      return;
    }

    let scheduleStrategy = ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL;
    const couponType = this.couponType?.toUpperCase() ?? 'NONE';
    const callEventType = this.callEventType?.toUpperCase() ?? 'NONE';
    
    if (couponType !== 'NONE' && callEventType === 'NONE') {
      scheduleStrategy = ScheduleStrategyKeys.COUPON_ONLY;
    } else if (couponType === 'NONE' && callEventType !== 'NONE') {
      scheduleStrategy = ScheduleStrategyKeys.CALL_ONLY;
    } else if (couponType === 'NONE' && callEventType === 'NONE') {
      scheduleStrategy = null;
    } else if (couponType !== 'NONE' && callEventType !== 'NONE') {
      const couponScheduleType = this.getComponentByKey(ScheduleType.COUPON)?.scheduleType?.value;

      if (couponScheduleType === SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY) {
        if (this.couponFrequency === FrequencyKeys.AT_MATURITY && this.callFrequency !== FrequencyKeys.AT_MATURITY) {
          scheduleStrategy = ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL;
        } else {
          scheduleStrategy = ScheduleStrategyKeys.CALL_FROM_COUPON; 
        }
      }
      else if (this.couponFrequency !== FrequencyKeys.AT_MATURITY && this.callFrequency === FrequencyKeys.AT_MATURITY) {
        scheduleStrategy = ScheduleStrategyKeys.CALL_FROM_COUPON;
      } else if (this.couponFrequency === FrequencyKeys.AT_MATURITY && this.callFrequency !== FrequencyKeys.AT_MATURITY) {
        scheduleStrategy = ScheduleStrategyKeys.COUPON_FROM_CALL;
      } else if (this.couponFrequency === FrequencyKeys.AT_MATURITY && this.callFrequency === FrequencyKeys.AT_MATURITY) {
        scheduleStrategy = ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL;
      }
    }

    this.form.patchValue({ scheduleStrategy });
  }

  observeFormsChanges(): void {
    this.keyDatesModalService.ngbModal.activeInstances
      .pipe(
        takeUntil(this.destroy$),
        pluck('0'),
        switchMap((modalInstance: NgbModalRef) =>
          modalInstance
            ? from(modalInstance.result).pipe(
                take(1),
                switchMap((result: string) => {
                  if (result !== 'cancel') {
                    return of(result).pipe(take(1));
                  }
                  return EMPTY;
                })
              )
            : EMPTY
        )
      )
      .subscribe(() => {
        this.addErrorsToMainForm();
      });

      this.form.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(250),
          tap(() => this.form.touched && this.addErrorsToMainForm())
        ).subscribe();

      this.scheduleComponents.changes
        .pipe(
          startWith(takeUntil(this.destroy$)),
      ).subscribe(() => this.removeInvalidSchedules());
  }

  addErrorsToMainForm(): void {
    this.eventSchedulesForm
      .get('schedules')
      .setErrors({ mainFormChanged: true });
    this.eventSchedulesForm['errorMessage'] =
      'You changed some key fields in the main form. Please, refresh the schedules.';
  }

  getSchedules(): void {
    if (
      !this.scheduleComponents.some((component) => component.hasEditedDates())
    ) {
      this.sendScheduleRefreshRequest();
      return;
    }

    this.modalService
      .openDialog({
        title: 'Warning',
        body: '<p>The current schedule has some modified dates. Refreshing the schedule will discard those dates.</p><p>Do you want to proceed?</p>',
        buttons: {
          positive: 'Yes',
          negative: 'No',
        },
        size: 'md',
      })
      .then(
        (result) => {
          if (result == '{"positive":1}') {
            this.sendScheduleRefreshRequest();
          }
        },
        (error) => {
          console.log(`Error occurred while refreshing schedule: ${error}`);
          console.log(error);
        }
      );
  }

  private getScheduleRefreshPayload(): any {
    return {
      scheduleStrategy: this.form.get(ScheduleEnum.scheduleStrategy).value,
      settlementCalendars:
        this.summaryForm?.get(SummaryEnum.settlementCalendars)?.value || [],
      tradingCalendars:
        this.summaryForm?.get(SummaryEnum.tradingCalendars)?.value || [],
      keyDates: ScheduleFormUtils.keyDatesForGetSchedulesPayload(this.summaryForm),
      underlyings: this.underlyingsForGetSchedulesPayload(),
    };
  }

  private sendScheduleRefreshRequest(): void {
    this.isRefreshing = true;
    let payload: any;

    try {
      payload = this.scheduleComponents.reduce(
        (acc: any, current: ScheduleComponent) => ({
          ...acc,
          [current.key]: current.getSchedulePayload(),
        }),
        this.getScheduleRefreshPayload()
      );

      if (!payload[ScheduleType.COUPON] || this.scheduleStrategyValue === ScheduleStrategyKeys.CALL_ONLY) {
        payload[ScheduleType.COUPON] = { type: 'NONE' };
      }

      if (!payload[ScheduleType.CALLABILITY] || this.scheduleStrategyValue === ScheduleStrategyKeys.COUPON_ONLY) {
        payload[ScheduleType.CALLABILITY] = { type: 'NONE' }
      }

      if (!payload[ScheduleType.PARTICIPATION]) {
        payload[ScheduleType.PARTICIPATION] = { 
          [ParticipationEnum.upside]: { type: 'NONE' },
          [ParticipationEnum.downside]: { type: 'NONE' },
          [ParticipationEnum.base]: { type: 'NONE' },
        }
      }

      payload = this.applyDerivedSchedule(payload);

    } catch (error) {
      this.isRefreshing = false;
      this.dcAlertInnerService.showAlert({
        type: 'danger',
        message: `Error: ${error.error?.userMessage || error.error?.details || GetErrorMessageFromResponse(error, 'Unexpected error while refreshing schedule')}`,
      });
      return;
    }

    this.productCaptureService
      .getSchedules(payload)
      .pipe(
        tap(() => {
          this.isRefreshing = false;
        }),
        catchError((error: any) => {
          this.isRefreshing = false;
          console.error(error);
          if (error instanceof HttpErrorResponse) {
            this.dcAlertInnerService.showAlert({
              type: 'danger',
              message: `Error: ${error.error.userMessage}: ${error.error?.details}`,
            });

            return EMPTY;
          }
        })
      )
      .subscribe((schedules: any) => {
        this.scheduleComponents.forEach((current: ScheduleComponent) =>
          this.updateProductData(current, schedules)
        );

        this.eventSchedulesForm.get('schedules').setErrors(null);
      });
  }

  applyDerivedSchedule(payload: any): any {
    const derivedValidValues = [
      ScheduleStrategyKeys.CALL_FROM_COUPON,
      ScheduleStrategyKeys.COUPON_FROM_CALL,
    ];

    const currentStrategy = ScheduleStrategyKeys[this.scheduleStrategyValue];

    if (!derivedValidValues.includes(currentStrategy)) {
      return payload;
    }

    const keys = {
      [ScheduleStrategyKeys.CALL_FROM_COUPON]: {
        scheduleKey: ScheduleType.CALLABILITY,
        derivedFrom: ScheduleType.COUPON,
      },
      [ScheduleStrategyKeys.COUPON_FROM_CALL]: {
        scheduleKey: ScheduleType.COUPON,
        derivedFrom: ScheduleType.CALLABILITY,
      },
    };

    const { scheduleKey, derivedFrom } = keys[currentStrategy];
    const { type } = payload[scheduleKey];

    if (!type || type === 'NONE') {
      return payload;
    }

    const derivedSchedule = payload[derivedFrom];
    const { scheduleType, frequency, pmtScheduleLag, pmtScheduleLagBase } = derivedSchedule;

    payload = {
      ...payload,
      [scheduleKey]: {
        ...payload[scheduleKey],
        scheduleType,
        pmtScheduleLag,
        pmtScheduleLagBase
      }
    }

    return payload;
  }

  underlyingsForGetSchedulesPayload(): UnderlyingsScheduler {
    const underlyingAssetsValues: UnderlyingAsset[] =
      this.underlyingAssetsGroup?.get(SummaryEnum.underlyingAssets)?.value ||
      [];

    const underlyingAssets = underlyingAssetsValues.map((asset) => ({
      assetId: asset.assetId,
      calendarCodes: asset.calendars || [],
      initialObsDateOverride: asset.initial_FixingDate,
    }));

    const underlyings = {
      assets: underlyingAssets,
      basketRollType:
        this.underlyingAssetsGroup.get(SummaryEnum.rollAs)?.value,
    };

    return underlyings;
  }

  updateProductData(schedule: ScheduleComponent, schedules?: any): void {
    if (schedules) {
      schedule.updateScheduleData(schedules);
    }

    const { schedulePayoffKey: type } = schedule;

    const scheduleOrigin = this.payoffForm.get(type);

    const scheduleOriginValue = { 
      [CouponEnum.schedule]: []
    };
    
    if (type !== PayoffEnum.participation) {
      scheduleOriginValue[CouponEnum.schedule] = schedule.getSchedule().value;
    }

    if (type === PayoffEnum.participation) {
      const valueToPatch = {
        [ParticipationEnum.downside]: {
          [CouponEnum.schedule]:
            scheduleOriginValue.schedule[ParticipationEnum.downside] ?? [],
        },
        [ParticipationEnum.upside]: {
          [CouponEnum.schedule]:
            scheduleOriginValue.schedule[ParticipationEnum.upside] ?? [],
        },
        [ParticipationEnum.base]: {
          [CouponEnum.schedule]:
            scheduleOriginValue.schedule[ParticipationEnum.base] ?? [],
        },
      };
      scheduleOrigin.patchValue(valueToPatch, { emitEvent: false });
    } else {
      scheduleOrigin.patchValue(scheduleOriginValue, { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    this.setupCommonFieldsOnFormChanges();
    this.observeFormsChanges();

    this.cdr.detectChanges();
  }

  getComponentByKey(key: string): ScheduleComponent {
    return this.scheduleComponents.find((component) => component.key === key);
  }

  getCouponValue(keyName: CouponEnum): string {
    return this.coupon?.get(keyName)?.value || '';
  }

  getCallabilityValue(keyName: CallabilityEnum): string {
    return this.callability?.get(keyName)?.value || '';
  }

  get couponFrequency(): string {
    return this.getCouponValue(CouponEnum.frequency);
  }

  get callFrequency(): string {
    return this.getCallabilityValue(CallabilityEnum.frequency);
  }

  get couponType(): string {
    return this.getCouponValue(CouponEnum.couponType);
  }

  get callEventType(): string {
     return this.getCallabilityValue(CallabilityEnum.callEventType);
  }

  get selfForm(): UntypedFormGroup {
    return this.form;
  }

  get eventSchedulesForm(): UntypedFormGroup {
    return this.productForm.get('eventSchedules') as UntypedFormGroup;
  }

  get scheduleStrategy(): UntypedFormControl {
    return this.summaryForm.get(ScheduleEnum.schedule).get(ScheduleEnum.scheduleStrategy) as UntypedFormControl;
  }

  get scheduleStrategyValue(): string {
    return this.scheduleStrategy?.value || '';
  }

  get coupon(): UntypedFormGroup {
    return this.payoffForm.get(PayoffEnum.coupon) as UntypedFormGroup;
  }

  get callability(): UntypedFormGroup {
    return this.payoffForm.get(PayoffEnum.callability) as UntypedFormGroup;
  }

  get participation(): UntypedFormGroup {
    return this.payoffForm.get(PayoffEnum.participation) as UntypedFormGroup;
  }

  get summaryForm(): UntypedFormGroup {
    return this.productForm.get(
      ProductCaptureGroupsEnum.summary
    ) as UntypedFormGroup;
  }

  get payoffForm(): UntypedFormGroup {
    return this.productForm.get(ProductCaptureGroupsEnum.payoff) as UntypedFormGroup;
  }

  get productTypeControl(): UntypedFormGroup {
    return this.summaryForm.get('productType') as UntypedFormGroup;
  }

  get accrualTypeControl(): UntypedFormControl {
    return this.form.get('accrualPeriodType') as UntypedFormControl;
  }

  get productType(): string {
    return this.productTypeControl.value;
  }

  get underlyingAssetsGroup(): UntypedFormControl {
    return this.summaryForm.get(
      SummaryEnum.underlyingAssetsGroup
    ) as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
